import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { Toaster } from 'react-hot-toast'
import { ScrollProvider } from './context/ScrollContext'
import Navbar from './components/Navbar'
import Landing from './pages/Landing'
const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Register = lazy(() => import('./pages/Register'))
const RegisterBusiness = lazy(() => import('./pages/RegisterBusiness'))
const RegisterInfluencer = lazy(() => import('./pages/RegisterInfluencer'))
const Help = lazy(() => import('./pages/Help'))
const About = lazy(() => import('./pages/About'))
const Contact = lazy(() => import('./pages/Contact'))
const Terms = lazy(() => import('./pages/Terms'))
const Privacy = lazy(() => import('./pages/Privacy'))
const ComingSoon = lazy(() => import('./pages/ComingSoon'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const Press = lazy(() => import('./pages/Press'))
const Brands = lazy(() => import('./pages/Brands'))
const Creators = lazy(() => import('./pages/Creators'))
const Compare = lazy(() => import('./pages/Compare'))
const Pricing = lazy(() => import('./pages/Pricing'))
const HowItWorks = lazy(() => import('./pages/HowItWorks'))
const GuidePayPerInteraction = lazy(() => import('./pages/GuidePayPerInteraction'))
const GuideMicroInfluencerMarketing = lazy(() => import('./pages/GuideMicroInfluencerMarketing'))
const GuideCpeBenchmarks = lazy(() => import('./pages/GuideCpeBenchmarks'))
const Guides = lazy(() => import('./pages/Guides'))
const BlogIndex = lazy(() => import('./pages/BlogIndex'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const GuideFindInfluencers2026 = lazy(() => import('./pages/GuideFindInfluencers2026'))
const GuideInfluencerMarketingROI = lazy(() => import('./pages/GuideInfluencerMarketingROI'))
const GuideInstagramInfluencerRates2026 = lazy(() => import('./pages/GuideInstagramInfluencerRates2026'))
const GuideTiktokCreatorMarketing2026 = lazy(() => import('./pages/GuideTiktokCreatorMarketing2026'))
const GuideInfluencerCampaignBrief = lazy(() => import('./pages/GuideInfluencerCampaignBrief'))
const Tools = lazy(() => import('./pages/Tools'))
const ToolEngagementRateCalculator = lazy(() => import('./pages/ToolEngagementRateCalculator'))
const ToolCreatorEarningsCalculator = lazy(() => import('./pages/ToolCreatorEarningsCalculator'))
const ToolCampaignBudgetCalculator = lazy(() => import('./pages/ToolCampaignBudgetCalculator'))
const ToolDisclosureGenerator = lazy(() => import('./pages/ToolDisclosureGenerator'))
const ToolRateCardGenerator = lazy(() => import('./pages/ToolRateCardGenerator'))
const CreatorMediaKit = lazy(() => import('./pages/CreatorMediaKit'))
const BusinessDashboard = lazy(() => import('./pages/BusinessDashboard'))
const InfluencerDashboard = lazy(() => import('./pages/InfluencerDashboard'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const AdminReviewQueue = lazy(() => import('./pages/admin/ReviewQueue'))
const PayoutSetupComplete = lazy(() => import('./pages/PayoutSetupComplete'))
const Referral = lazy(() => import('./pages/Referral'))
const CampaignSummary = lazy(() => import('./pages/CampaignSummary'))
import { AuthProvider, useAuth } from './context/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import SupportChatWidget from './components/SupportChatWidget'
import RouteSeo from './components/RouteSeo'
import { initSmoothScroll, destroySmoothScroll, smoothScrollTo } from './lib/smoothScroll'

function ScrollToTop() {
    const { pathname, hash } = useLocation()
    useEffect(() => {
        if (hash) {
            const el = document.getElementById(hash.slice(1))
            if (el) {
                smoothScrollTo(el)
                return
            }
        }
        smoothScrollTo(0, { immediate: true })
    }, [pathname, hash])
    return null
}

function RouteFallback() {
    return (
        <div
            aria-hidden="true"
            style={{
                minHeight: 'calc(100vh - var(--header-height))',
                background: 'var(--bg)',
            }}
        />
    )
}

function AnimatedRoutes() {
    const location = useLocation()
    const isDashboardShell =
        location.pathname.startsWith('/dashboard') ||
        location.pathname.startsWith('/admin')

    return (
        <main className={`main-content${isDashboardShell ? ' main-content--dashboard' : ''}`} id="main-content">
            <Toaster position="top-right" />
            <Suspense fallback={<RouteFallback />}>
                <Routes location={location}>
                    <Route path="/" element={<Landing />} />

                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/register/business" element={<RegisterBusiness />} />
                    <Route path="/register/influencer" element={<RegisterInfluencer />} />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <DashboardRedirect />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/business/*"
                        element={
                            <ProtectedRoute allowedRoles={['business']}>
                                <BusinessDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard/influencer/*"
                        element={
                            <ProtectedRoute allowedRoles={['influencer']}>
                                <InfluencerDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/brands" element={<Brands />} />
                    <Route path="/creators" element={<Creators />} />
                    <Route path="/compare" element={<Compare />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/help" element={<Help />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route
                        path="/payout-setup-complete"
                        element={
                            <ProtectedRoute>
                                <PayoutSetupComplete />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/referral"
                        element={
                            <ProtectedRoute allowedRoles={['influencer']}>
                                <Referral />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <AdminDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/review-queue"
                        element={
                            <ProtectedRoute allowedRoles={['admin']}>
                                <AdminReviewQueue />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/coming-soon" element={<ComingSoon />} />
                    <Route path="/press" element={<Press />} />
                    <Route path="/guides" element={<Guides />} />
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/guides/pay-per-interaction-influencer-marketing" element={<GuidePayPerInteraction />} />
                    <Route path="/guides/micro-influencer-marketing" element={<GuideMicroInfluencerMarketing />} />
                    <Route path="/guides/cpe-benchmarks-2026" element={<GuideCpeBenchmarks />} />
                    <Route path="/guides/how-to-find-influencers-2026" element={<GuideFindInfluencers2026 />} />
                    <Route path="/guides/influencer-marketing-roi" element={<GuideInfluencerMarketingROI />} />
                    <Route path="/guides/instagram-influencer-rates-2026" element={<GuideInstagramInfluencerRates2026 />} />
                    <Route path="/guides/tiktok-creator-marketing-2026" element={<GuideTiktokCreatorMarketing2026 />} />
                    <Route path="/guides/influencer-campaign-brief" element={<GuideInfluencerCampaignBrief />} />
                    <Route path="/tools" element={<Tools />} />
                    <Route path="/tools/engagement-rate-calculator" element={<ToolEngagementRateCalculator />} />
                    <Route path="/tools/creator-earnings-calculator" element={<ToolCreatorEarningsCalculator />} />
                    <Route path="/tools/campaign-budget-calculator" element={<ToolCampaignBudgetCalculator />} />
                    <Route path="/tools/disclosure-generator" element={<ToolDisclosureGenerator />} />
                    <Route path="/tools/rate-card-generator" element={<ToolRateCardGenerator />} />
                    <Route path="/creator/:username" element={<CreatorMediaKit />} />
                    <Route
                        path="/campaigns/:id/summary"
                        element={
                            <ProtectedRoute>
                                <CampaignSummary />
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </Suspense>
        </main>
    )
}

const DashboardRedirect = () => {
    const { user } = useAuth()
    if (user?.role === 'business') return <Navigate to="/dashboard/business" replace />
    if (user?.role === 'influencer') return <Navigate to="/dashboard/influencer" replace />
    if (user?.role === 'admin') return <Navigate to="/admin" replace />
    return <Navigate to="/login" replace />
}


function App() {
    useEffect(() => {
        initSmoothScroll()
        return () => destroySmoothScroll()
    }, [])

    return (
        <ErrorBoundary>
            <a href="#main-content" className="skip-link">Skip to content</a>
            <AuthProvider>
                <ScrollProvider>
                    <RouteSeo />
                    <ScrollToTop />
                    <Navbar />
                    <AnimatedRoutes />
                    <SupportChatWidget />
                </ScrollProvider>
            </AuthProvider>
        </ErrorBoundary>
    )
}

export default App
